import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCancelCourierPickup } from '@/api/domains/courier-pickups';
import type { CourierPickup } from '@/api/types/pickups';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toApiProblem, toastProblem } from '@/lib/errors';

type Props = {
  pickup: CourierPickup | null;
  onOpenChange: (open: boolean) => void;
};

export function CancelPickupOrderDialog({ pickup, onOpenChange }: Props) {
  const { t } = useTranslation('pickup-orders');
  const cancel = useCancelCourierPickup();

  const confirm = () => {
    if (!pickup) return;
    cancel.mutate(pickup.id, {
      onSuccess: () => {
        toast.success(t('cancel.success'));
        onOpenChange(false);
      },
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  return (
    <AlertDialog open={pickup !== null} onOpenChange={(open) => !open && onOpenChange(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('cancel.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('cancel.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={cancel.isPending}>
            {t('cancel.keep')}
          </Button>
          <Button
            className="bg-[#e11d48] text-white hover:bg-[#be123c]"
            onClick={confirm}
            disabled={cancel.isPending}
          >
            {cancel.isPending ? t('cancel.cancelling') : t('cancel.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
